import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Service, AddOn } from '../types';
import { getServices, getAddOns } from '../services/supabase';
import './Services.css';


const Services: React.FC = () => {
  const [services, setServices] = useState<(Service & { add_ons?: string[] })[]>([]);
  const [addOns, setAddOns] = useState<AddOn[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesData, addOnsData] = await Promise.all([
          getServices(),
          getAddOns(),
        ]);
        setServices(servicesData);
        setAddOns(addOnsData);
      } catch (error) {
        console.error('Error fetching services or add-ons:', error);
        setServices([]);
        setAddOns([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes} hour(s) max`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };



  // See More logic for add-on descriptions (must be before any returns)
  const [expandedAddOns, setExpandedAddOns] = useState<{ [key: string]: boolean }>({});
  const MAX_DESC_LENGTH = 80;
  const handleSeeMore = (addOnId: string) => {
    setExpandedAddOns(prev => ({ ...prev, [addOnId]: !prev[addOnId] }));
  };

  if (loading) {
    return (
      <div className="services-loading">
        <div className="loading-spinner"></div>
        <p>Loading our services...</p>
      </div>
    );
  }

  // Debug: Show message if no services found
  if (!services.length) {
    return (
      <div className="services-empty-debug">
        <h2>No services found</h2>
        <p>Check if your database has service data and that the fetch is working.</p>
        <pre style={{textAlign: 'left', background: '#eee', padding: '1em', overflowX: 'auto'}}>
          {JSON.stringify(services, null, 2)}
        </pre>
      </div>
    );
  }

  return (
    <div className="services">
      <div className="container">
        <div className="services-header">
          <h1 className="services-title">Our Services</h1>
          <p className="services-description">
            Choose from our range of professional car detailing services, 
            each designed to meet your specific needs and budget.
          </p>
        </div>

        <div className="services-grid">
          {services.map((service, idx) => {
            const serviceAddOns = addOns.filter(a => a.service_id === service.id);

            return (
              <div key={service.id} className="service-card">
                <div className="service-image">
                  {service.image_url ? (
                    <img src={service.image_url} alt={service.name} />
                  ) : idx === 0 ? (
                    <img src={process.env.PUBLIC_URL + '/porsche_in.png'} alt="Special Service" className="service-placeholder-img" />
                  ) : idx === 1 ? (
                    <img src={process.env.PUBLIC_URL + '/2025-06-20.webp'} alt="Porsche Interior" className="service-placeholder-img" />
                  ) : idx === 2 ? (
                    <img src={process.env.PUBLIC_URL + '/inter.webp'} alt="Lack Service" className="service-placeholder-img" />
                  ) : (
                    <img src={process.env.PUBLIC_URL + '/lack.webp'} alt="Interior Service" className="service-placeholder-img" />
                  )}
                </div>
                <div className="service-content">
                  <h3 className="service-name">{service.name}</h3>
                  <p className="service-description">{service.description}</p>
                  {/* Add-ons section */}
                  {serviceAddOns.length > 0 && (
                    <div className="service-addons">
                      <h4 className="service-addons-heading">Add-ons:</h4>
                      <ul>
                        {serviceAddOns.map(addOn => {
                          const isLong = addOn.description.length > MAX_DESC_LENGTH;
                          const expanded = expandedAddOns[addOn.id];
                          return (
                            <li key={addOn.id} className="service-addon-item">
                              <strong>{addOn.name}</strong>
                              {isLong && !expanded ? (
                                <>
                                  <button
                                    className="see-desc-btn"
                                    onClick={() => handleSeeMore(addOn.id)}
                                  >
                                    See Description
                                  </button>
                                </>
                              ) : (
                                <span>: {addOn.description}</span>
                              )}
                              {isLong && expanded && (
                                <button
                                  className="see-desc-btn"
                                  onClick={() => handleSeeMore(addOn.id)}
                                >
                                  Hide Description
                                </button>
                              )}
                              {' '}(${addOn.price})
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  <div className="service-details">
                    <div className="service-duration">
                      <span className="detail-icon">⏱️</span>
                      {formatDuration(service.duration)}
                    </div>
                    <div className="service-price">
                      <span className="price-symbol">$</span>
                      <span className="price-amount">{service.price}</span>
                    </div>
                  </div>
                  <Link 
                    to={`/book?service=${service.id}`}
                    className="service-book-btn"
                  >
                    Book This Service
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
        <div className="services-questions">
          <h3>Questions?</h3>
          <p>Contact us for custom packages or special requests.</p>
          <div className="services-contact">
            <span>📞 (904) 487-1591</span>
            <span>📧 andersondequan5@gmail.com</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;